import {
  AbstractClient,
  Client,
  ClientErrorResponse,
  ClientResponse,
  ClientStatus,
  Conversation,
  ConversationEventType,
  ConversationMessage,
  ConversationMessagesResponseData,
  ObservabilityConfig,
  ConversationSyncStorageMode,
  ConversationActionParams,
  ConversationChannel,
  ConversationParticipant,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  InProgressParams,
  InProgressResponse,
  LeaveConversationParams,
  MessagePartActionParams,
  MessagePartSubmitParams,
  SendConversationMessageParams,
  SendConversationTypingParams,
  SendFeedbackParams,
  StartConversationParams,
  StartConversationResponse,
  ClientUrls,
  METRIC,
  isNetworkError,
  isOnline,
  SelectOptionAction,
} from '@stripe-internal/conversations-client';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  firstValueFrom,
  Observable,
} from 'rxjs';
import LRUCache from 'lru-cache';
import {buildClientHandlers, ClientHandlers} from './api/ClientHandlers';

const KB = 1024;

const DELEGATE_FIRST_MESSAGE_TO_SERVER_ERROR_METRIC =
  'conversations_api_client.start_conversation.delegate_first_message_to_server.error';

const isSelectOptionAction = (
  action: ConversationActionParams['action'],
): action is SelectOptionAction =>
  action?.name === 'select-option' &&
  typeof action.selectedOptionId === 'string';

export type ConversationsApiClientOptions = {
  clientFetch?: typeof fetch;
  urls?: Partial<ClientUrls>;
  observability?: ObservabilityConfig;
  enableUhPendingInput?: boolean;
  userCountry?: string;
  enableRegionHeaderRouting?: boolean;
  enableConversationsThreads?: boolean;
};

export const ConversationsApiAssistantUser: ConversationParticipant = {
  id: 'assistant',
  role: 'bot',
  displayName: 'Assistant',
};

export class ConversationsApiClient extends AbstractClient implements Client {
  private readonly METRIC_TAGS = {client_type: 'conversations-api'};

  channel: ConversationChannel = 'bot';

  type = 'conversations-api';

  syncStorageMode: ConversationSyncStorageMode = 'sync';

  // An ID selects a Threads conversation's signal. The undefined key is reserved
  // for the legacy client-wide signal: SupportConversationsClient still calls
  // the pending APIs without an ID. It does not represent an unidentified issue.
  private pendingByConversation = new Map<
    string | undefined,
    BehaviorSubject<boolean>
  >();

  private handlers: ClientHandlers;

  private currentUserParticipant?: ConversationParticipant;

  private messageCache: LRUCache<string, ConversationMessage> = new LRUCache({
    max: 500,
    maxSize: 512 * KB,
    sizeCalculation: (o) =>
      Object.values(o).reduce((acc, p) => acc + (p?.length || 1), 0),
  });

  private userCountry?: string;

  private enableRegionHeaderRouting: boolean;

  constructor({
    clientFetch,
    urls,
    observability,
    enableUhPendingInput,
    userCountry,
    enableRegionHeaderRouting = false,
    enableConversationsThreads,
  }: ConversationsApiClientOptions = {}) {
    super({
      clientFetch,
      urls,
      observability,
    });

    this.status$.next(
      isOnline() ? ClientStatus.Connected : ClientStatus.Offline,
    );

    // since we don't have a socket we're reconnecting,
    // when we come back online set the status to connected
    this.status$.pipe(debounceTime(50)).subscribe(() => {
      if (this.getStatus() === ClientStatus.Reconnecting) {
        this.status$.next(ClientStatus.Connected);
      }
    });

    this.userCountry = userCountry;
    this.enableRegionHeaderRouting = enableRegionHeaderRouting;
    this.handlers = buildClientHandlers({
      client: this,
      request: this.fetch,
      reportWarning: (error, scope) => this.errors.reportWarning(error, scope),
      emitConversationEvent: (event) => this.emitNewConversationEvent(event),
      emitDelegateFirstMessageToServerFailureMetric: (tags) =>
        this.incrementAndTrack(
          DELEGATE_FIRST_MESSAGE_TO_SERVER_ERROR_METRIC,
          tags,
        ),
      getParticipant: () => this.currentUserParticipant,
      getAssistantParticipant: () => ConversationsApiAssistantUser,
      getCachedMessage: (conversationId, messageId) =>
        this.getCachedMessage(conversationId, messageId),
      baseUrl: this.conversationUrl,
      enableUhPendingInput,
      getCreateConversationHeaders: () => this.getCreateConversationHeaders(),
      enableConversationsThreads,
    });
  }

  async getConversation(
    params: Parameters<ClientHandlers['getConversation']>[0],
  ): ReturnType<ClientHandlers['getConversation']> {
    this.currentUserParticipant = params.participant;
    try {
      const response = await this.handlers.getConversation(params);
      if ('data' in response) {
        Object.values(response.data.messages).forEach((message) =>
          this.cacheMessage(response.data.id, message),
        );
      }
      return response;
    } catch (e) {
      return this.reportConversationException(
        e,
        'exception retrieving conversation',
        params.conversationId,
      );
    }
  }

  async startConversation(
    params: StartConversationParams,
  ): Promise<StartConversationResponse> {
    if (params.participant) {
      this.currentUserParticipant = params.participant;
    }
    try {
      const response = await this.handlers.startConversation(params);
      if (response.error) {
        this.incrementAndTrack(METRIC.start_conversation_error);
      } else {
        const conversationId = (response.data as {conversation?: Conversation})
          ?.conversation?.id;
        this.incrementAndTrack(METRIC.start_conversation_success, {
          ...(conversationId
            ? {universalHelpConversationId: conversationId}
            : {}),
          audience: params.context?.audience ?? '',
        });
      }
      this.emitStartConversationEvents(response);
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.start_conversation_error);
      return this.reportConversationException(
        e,
        'exception starting conversation',
        params.conversationId,
      );
    }
  }

  async leaveConversation(
    params: LeaveConversationParams,
  ): Promise<ClientResponse> {
    try {
      const response = await this.handlers.leaveConversation(params);
      this.trackResponseMetric(
        response,
        METRIC.leave_conversation_success,
        METRIC.leave_conversation_error,
      );
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.leave_conversation_error);
      return this.reportConversationException(
        e,
        'exception leaving conversation',
        params.conversationId,
      );
    }
  }

  async getConversationMessages(
    params: GetConversationMessagesParams,
  ): Promise<GetConversationMessagesResponse> {
    const {conversationId, messageIds} = params;

    // Cache-first: if every requested id is already cached, skip the network.
    if (messageIds && messageIds.length > 0) {
      const cachedMessages: Record<string, ConversationMessage> = {};
      let cachedCount = 0;
      messageIds.forEach((messageId) => {
        const cached = this.getCachedMessage(conversationId, messageId);
        if (cached) {
          cachedCount += 1;
          cachedMessages[cached.id] = cached;
        }
      });
      if (cachedCount === messageIds.length) {
        return {code: 200, data: {messages: cachedMessages}};
      }
    }

    const response = await this.handlers.getConversationMessages(params);

    // Warm-on-fetch: cache messages the server returned so later reads hit.
    if (!response.error) {
      const messagesData = response.data as
        | ConversationMessagesResponseData
        | undefined;
      if (messagesData?.messages) {
        Object.values(messagesData.messages).forEach((message) =>
          this.cacheMessage(conversationId, message),
        );
      }
    }

    return response;
  }

  getConversationParticipants(
    params?: GetConversationParticipantsParams,
  ): Promise<GetConversationParticipantsResponse> {
    return this.handlers.getConversationParticipants(params);
  }

  async sendConversationMessage(
    params: SendConversationMessageParams,
  ): Promise<ClientResponse> {
    this.setPending(params.conversationId, true);
    try {
      const response = await this.handlers.sendConversationMessage(params);
      this.trackResponseMetric(
        response,
        METRIC.send_message_success,
        METRIC.send_message_error,
      );
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.send_message_error);
      return this.reportConversationException(
        e,
        'exception sending conversation message',
        params.conversationId,
      );
    } finally {
      this.setPending(params.conversationId, false);
    }
  }

  async sendConversationTyping(
    params?: SendConversationTypingParams,
  ): Promise<ClientResponse> {
    const response = await this.handlers.sendConversationTyping(params);
    if (!response.error && params) {
      this.emitNewConversationEvent({
        name: 'conversation-typing-started',
        conversationId: params.conversationId,
        participantId: params.participantId,
      });
    }
    return response;
  }

  async sendFeedback(params: SendFeedbackParams): Promise<ClientResponse> {
    try {
      const response = await this.handlers.sendFeedback(params);
      this.trackResponseMetric(
        response,
        METRIC.send_feedback_success,
        METRIC.send_feedback_error,
      );
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.send_feedback_error);
      return this.reportConversationException(
        e,
        'exception sending feedback',
        params.conversationId,
      );
    }
  }

  async action(params: ConversationActionParams): Promise<ClientResponse> {
    if (
      isSelectOptionAction(params.action) &&
      params.action.selectedOptionId.startsWith(
        'link_account_delete_option_reason_',
      )
    ) {
      this.incrementAndTrack(METRIC.link_accounts_deleted, {
        reason: params.action.selectedOptionId,
      });
    }

    this.setPending(params.conversationId, true);
    try {
      const response = await this.handlers.action(params);
      this.trackResponseMetric(
        response,
        METRIC.action_success,
        METRIC.action_error,
      );
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.action_error);
      return this.reportConversationException(
        e,
        'exception sending conversation action',
        params.conversationId,
      );
    } finally {
      this.setPending(params.conversationId, false);
    }
  }

  inProgress(params: InProgressParams): Promise<InProgressResponse> {
    return this.handlers.inProgress(params);
  }

  pollConversation(params: Parameters<ClientHandlers['pollConversation']>[0]) {
    return this.handlers.pollConversation(params);
  }

  actOnMessagePart = (
    params: MessagePartActionParams,
  ): Promise<ClientResponse> => {
    return this.handlers.actOnMessagePart(params);
  };

  submitMessagePart = ({
    conversationId,
    submission,
  }: MessagePartSubmitParams): Promise<ClientResponse> => {
    return this.handlers.submitMessagePart({
      conversationId,
      submission,
    });
  };

  private pendingForConversation(
    conversationId?: string,
  ): BehaviorSubject<boolean> {
    let pending = this.pendingByConversation.get(conversationId);
    if (!pending) {
      pending = new BehaviorSubject(false);
      this.pendingByConversation.set(conversationId, pending);
    }
    return pending;
  }

  private setPending(conversationId: string, pending: boolean): void {
    this.pendingForConversation(conversationId).next(pending);
    // Legacy callers observe every request, while Threads observes only its ID.
    // Preserve the existing boolean semantics: any completion clears the legacy
    // signal, even if another conversation still has a request in flight.
    this.pendingForConversation(undefined).next(pending);
  }

  // Calls without an ID retain the legacy client-wide boolean behavior.
  public waitForPendingMessagesHandled(
    conversationId?: string,
  ): Promise<boolean> {
    return firstValueFrom(
      this.busyObservable(conversationId).pipe(
        filter((pending) => !pending),
        debounceTime(250),
      ),
    );
  }

  busyObservable(conversationId?: string): Observable<boolean> {
    return this.pendingForConversation(conversationId).asObservable();
  }

  isPending(conversationId?: string): boolean {
    return this.pendingForConversation(conversationId).getValue();
  }

  // eslint-disable-next-line class-methods-use-this
  async disconnect(): Promise<void> {
    // no-op
  }

  private incrementAndTrack(
    metricName: string,
    tags: Record<string, string | number | boolean> = {},
  ): void {
    const metricTags = {...this.METRIC_TAGS, ...tags};
    this.metrics.increment(metricName, metricTags);
    this.analytics.track(metricName, metricTags);
  }

  private trackResponseMetric(
    response: ClientResponse,
    successMetricName: string,
    errorMetricName: string,
  ): void {
    if (response.error) {
      this.incrementAndTrack(errorMetricName);
      return;
    }
    this.incrementAndTrack(successMetricName);
  }

  protected emitNewConversationEvent<T extends ConversationEventType>(
    event: T,
  ): void {
    if (
      (event.name === 'conversation-message-added' ||
        event.name === 'conversation-message-updated') &&
      event.message
    ) {
      this.cacheMessage(event.conversationId, event.message);
    }
    super.emitNewConversationEvent(event);
  }

  private cacheMessage(
    conversationId: string,
    message: ConversationMessage,
  ): void {
    const key = `${conversationId}-${message.id}`;
    this.messageCache.set(key, message);
  }

  private getCachedMessage(
    conversationId: string,
    messageId: string,
  ): ConversationMessage | undefined {
    const key = `${conversationId}-${messageId}`;
    return this.messageCache.get(key);
  }

  private clearCachedMessage(conversationId: string, messageId: string): void {
    const key = `${conversationId}-${messageId}`;
    this.messageCache.delete(key);
  }

  private emitStartConversationEvents(
    response: StartConversationResponse,
  ): void {
    const conversation = (response.data as {conversation?: Conversation})
      ?.conversation;
    if (!conversation || response.error) {
      return;
    }
    this.emitNewConversationEvent({
      name: 'conversation-started',
      conversationId: conversation.id,
    });
    this.emitNewConversationEvent({
      name: 'conversation-set-context-values',
      conversationId: conversation.id,
      values: conversation.context,
    });
    Object.values(conversation.messages).forEach((message) => {
      this.emitNewConversationEvent({
        name: 'conversation-message-added',
        conversationId: conversation.id,
        participantId: message.participant.id,
        messageId: message.id,
        message,
      });
    });
  }

  private getStripeRegion(): string {
    return this.userCountry === 'IN' ? 'bom' : 'northwest';
  }

  private getCreateConversationHeaders(): Record<string, string> {
    if (!this.enableRegionHeaderRouting) {
      return {};
    }

    return {'stripe-region': this.getStripeRegion()};
  }

  private async reportConversationException(
    e: unknown,
    context: string,
    conversationId: string | undefined,
  ): Promise<ClientErrorResponse> {
    const errorMessage = (e as Error).message ?? String(e);
    if (isNetworkError(e)) {
      return this.error(408, errorMessage, this.METRIC_TAGS);
    }

    const error = `${context}: ${errorMessage}`;
    const clientErrorResponse = await this.error(500, error, {
      ...this.METRIC_TAGS,
      assistant_error_screen: true,
    });
    // Creation may fail before an ID exists. Its caller receives the response;
    // broadcasting that failure would interrupt unrelated conversations.
    if (conversationId) {
      this.emitNewConversationEvent({
        name: 'conversation-exception',
        conversationId,
        error,
        sentryEventId: clientErrorResponse.sentryEventId,
      });
    }
    return clientErrorResponse;
  }
}
